import React, { useState, useEffect, useCallback, useMemo } from "react";
import api, {
  fetchPortStatusCached,
  fetchGatewayListFromDB,
  staffSwitchPort,
} from "./api";
import ActivationQueuePanel from "./features/gateway-management/components/ActivationQueuePanel";
import { usePortActivationStatus } from "./shared/portActivation";

/**
 * COMBINED PortStatus - Grid View + Card View
 *
 * Features:
 * - Toggle between Grid View (circles) and Card View (details)
 * - Clear status display for all 64 ports
 * - Gateway selector
 * - MANUAL refresh button only (no auto-refresh)
 * - Queue window showing activation queues
 * - Green indicator for active ports
 */

const PortStatus = () => {
  const [gateways, setGateways] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [portStatus, setPortStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState("Never");

  // Gateway dimensions from status response (max-ports = groups, max-slot = slots per group)
  const [gatewayMaxPorts, setGatewayMaxPorts] = useState(64);
  const [gatewayMaxSlots, setGatewayMaxSlots] = useState(8);

  // Activity cache: which ports are currently active
  const [activeSlots, setActiveSlots] = useState({});

  // Queue window
  const [queueOpen, setQueueOpen] = useState(false);
  const [queues, setQueues] = useState({});
  const [queuesLoading, setQueuesLoading] = useState(false);

  // Activation queue inspector (live timers, stuck detection, event log)
  const [activationQueuePanelOpen, setActivationQueuePanelOpen] = useState(false);

  // Gateway status tracking
  const [gatewayStatuses, setGatewayStatuses] = useState({}); // gw-id -> {online, lastSeen, lastError, lastChanged}
  const [gatewayEvents, setGatewayEvents] = useState([]); // [{time, gateway, event, type}]
  const [gatewayStatusOpen, setGatewayStatusOpen] = useState(true);

  // View toggle
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "card"

  // Staff: activate port (switch slot on device)
  const [activatingPort, setActivatingPort] = useState(null); // e.g. "1.03"
  const [switchError, setSwitchError] = useState(null);
  const [switchSuccess, setSwitchSuccess] = useState(null);
  const isStaff = typeof localStorage !== "undefined" && localStorage.getItem("role") === "staff";

  const { portStatus: activationByPortId } = usePortActivationStatus({ scope: "staff" });

  const activationSummary = useMemo(() => {
    const counts = { active: 0, queued: 0, activating: 0, inactive: 0, handing_over: 0 };
    Object.values(activationByPortId || {}).forEach((entry) => {
      const s = entry?.status || "inactive";
      if (s in counts) counts[s]++;
      else counts.inactive++;
    });
    return counts;
  }, [activationByPortId]);

  // Load available gateways
  useEffect(() => {
    async function loadGateways() {
      try {
        const list = await fetchGatewayListFromDB();
        const formatted = list.map((gw) => ({
          id: `gw-${gw.id}`,
          name: gw.name || `Gateway ${gw.id}`,
        }));
        setGateways(formatted);

        if (!selectedGateway && formatted.length > 0) {
          setSelectedGateway(formatted[0].id);
        }
      } catch (e) {
        console.error("Failed to load gateways:", e);
        setError("Failed to load gateway list");
      }
    }
    loadGateways();
  }, []);

  // Determine active slots from gateway status (more accurate than activity cache)
  const determineActiveSlots = useCallback((statusArray) => {
    const active = {};

    statusArray.forEach((slot) => {
      const [portNum, slotNum] = String(slot.port || "").split(".");
      const p = parseInt(portNum, 10);

      // Use gateway's active field directly (most accurate)
      if (slot.active === 1) {
        active[p] = parseInt(slotNum, 10);
      }
    });

    setActiveSlots(active);
  }, []);

  // Fetch queues
  const fetchQueues = useCallback(async () => {
    try {
      setQueuesLoading(true);
      const res = await api.get("/api/ports/queue");
      const data = res?.data || {};
      const q = data.queues || {};
      setQueues(q);
    } catch (e) {
      console.error("Failed to load queues:", e);
    } finally {
      setQueuesLoading(false);
    }
  }, []);

  // Poll all gateways via backend health endpoint (MultiGatewayManager circuit breaker)
  const checkAllGatewayStatuses = useCallback(async () => {
    if (gateways.length === 0) return;

    try {
      const resp = await api.get("/api/gateway-health");
      const healthData = resp.data || {};
      const now = new Date();
      const newEvents = [];

      setGatewayStatuses((prevStatuses) => {
        const newStatuses = { ...prevStatuses };

        gateways.forEach((gw) => {
          const health = healthData[gw.id];
          const online = health ? health.online : null;
          const prev = prevStatuses[gw.id];

          if (health && (!prev || prev.online !== online)) {
            newEvents.push({
              time: now.toLocaleTimeString(),
              gateway: gw.name,
              gatewayId: gw.id,
              event: online ? "🟢 Connected" : "🔴 Disconnected",
              type: online ? "connected" : "disconnected",
              error: health.paused
                ? `paused until ${health.paused_until}`
                : health.last_error_at
                  ? `last error ${health.last_error_at}`
                  : null,
            });
          }

          newStatuses[gw.id] = {
            online: health ? health.online : null,
            failure_streak: health?.failure_streak || 0,
            paused: health?.paused || false,
            lastSeen: online ? now : prev?.lastSeen || null,
            lastChanged:
              health && (!prev || prev.online !== online)
                ? now
                : prev?.lastChanged || now,
            name: gw.name,
          };
        });

        return newStatuses;
      });

      if (newEvents.length > 0) {
        setGatewayEvents((prev) => [...newEvents, ...prev].slice(0, 100));
      }
    } catch (e) {
      console.error("Failed to check gateway health:", e);
    }
  }, [gateways]);

  // Fetch port status for selected gateway
  const refreshStatus = useCallback(async () => {
    if (!selectedGateway) return;

    try {
      setLoading(true);
      setError(null);

      // Get status for this gateway
      const response = await fetchPortStatusCached(selectedGateway);

      if (response.error) {
        setError(response.error);
        setPortStatus([]);
      } else {
        const statusArray = response.status || [];
        setPortStatus(statusArray);
        setLastUpdate(new Date().toLocaleTimeString());

        // Use gateway dimensions from response (16x32 for NPA1 512-port, 64x8 for 64-port)
        const maxPorts = response["max-ports"] != null ? Number(response["max-ports"]) : 64;
        const maxSlots = response["max-slot"] != null ? Number(response["max-slot"]) : 8;
        setGatewayMaxPorts(maxPorts);
        setGatewayMaxSlots(maxSlots);

        // Determine active slots from gateway data (more accurate)
        determineActiveSlots(statusArray);
      }

      // Load queues if window is open
      if (queueOpen) {
        await fetchQueues();
      }
    } catch (err) {
      console.error("Error refreshing status:", err);
      setError(err.message || "Failed to refresh status");
    } finally {
      setLoading(false);
    }
  }, [selectedGateway, determineActiveSlots, queueOpen, fetchQueues]);

  // Initial refresh when gateway changes; clear staff switch feedback
  useEffect(() => {
    setSwitchError(null);
    setSwitchSuccess(null);
    if (selectedGateway) {
      refreshStatus();
    }
  }, [selectedGateway]);

  // Load queues when queue window opens
  useEffect(() => {
    if (queueOpen) {
      fetchQueues();
    }
  }, [queueOpen, fetchQueues]);

  // Poll gateway statuses every 30 seconds
  useEffect(() => {
    if (gateways.length === 0) return;
    checkAllGatewayStatuses();
    const interval = setInterval(checkAllGatewayStatuses, 30000);
    return () => clearInterval(interval);
  }, [gateways, checkAllGatewayStatuses]);

  // Staff: switch active slot on gateway
  const handleActivatePort = useCallback(
    async (portStr) => {
      if (!selectedGateway || !portStr) return;
      setSwitchError(null);
      setSwitchSuccess(null);
      setActivatingPort(portStr);
      try {
        await staffSwitchPort(selectedGateway, portStr);
        setSwitchSuccess(`Port ${portStr} activated.`);
        await refreshStatus();
      } catch (err) {
        setSwitchError(err.response?.data?.detail || err.message || "Switch failed");
      } finally {
        setActivatingPort(null);
      }
    },
    [selectedGateway, refreshStatus]
  );

  // Status label
  const getStatusLabel = (statusCode) => {
    const labels = {
      0: "No SIM",
      1: "Idle",
      2: "Registering",
      3: "Registered",
      4: "Connected",
      5: "Error",
    };
    return labels[statusCode] || "Unknown";
  };

  // Status color for badge
  const getStatusColor = (statusCode, isActive) => {
    if (isActive) return "bg-success";
    switch (statusCode) {
      case 0:
        return "bg-secondary";
      case 1:
        return "bg-warning text-dark";
      case 3:
        return "bg-info";
      case 4:
        return "bg-primary";
      case 5:
        return "bg-danger";
      default:
        return "bg-light text-dark";
    }
  };

  // For each port, find its current status and active slot
  const getPortData = (portNumber) => {
    const portSlots = portStatus.filter((p) => {
      const [portNum] = String(p.port || "").split(".");
      return parseInt(portNum, 10) === portNumber;
    });

    if (portSlots.length === 0) return null;

    const activeSlot = activeSlots[String(portNumber)];

    let displayData =
      portSlots.find((p) => {
        const [, slotNum] = String(p.port || "").split(".");
        return parseInt(slotNum, 10) === activeSlot;
      }) || portSlots[0];

    return {
      status: displayData.st || 0,
      signal: displayData.sig || 0,
      operator: displayData.opr || "---",
      port: displayData.port || `${portNumber}.01`,
      isActive: activeSlot !== null,
    };
  };

  // Create grid for grid view
  const createGrid = (statusArray, maxPorts = 64, maxSlots = 8) => {
    const grid = {};

    for (let p = 1; p <= maxPorts; p++) {
      grid[p] = {};
      for (let s = 1; s <= maxSlots; s++) {
        grid[p][s] = { active: 0, data: null };
      }
    }

    statusArray.forEach((slot) => {
      const [portNum, slotNum] = String(slot.port || "").split(".");
      const p = parseInt(portNum, 10);
      const s = parseInt(slotNum, 10);

      if (p >= 1 && p <= maxPorts && s >= 1 && s <= maxSlots) {
        grid[p][s] = {
          active: slot.active || (slot.st === 3 || slot.st === 4 ? 1 : 0),
          data: slot,
        };
      }
    });

    return grid;
  };

  // Render grid table (circles)
  const renderGridTable = (grid, maxPorts = 64, maxSlots = 8) => {
    return (
      <div style={{ overflowX: "auto", maxHeight: "650px" }}>
        <table
          style={{
            borderCollapse: "collapse",
            fontSize: "10px",
            minWidth: "100%",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  padding: "4px 2px",
                  border: "1px solid #e0e0e0",
                  background: "#f0f0f0",
                  position: "sticky",
                  top: 0,
                }}
              ></th>
              {Array.from({ length: maxPorts }, (_, i) => i + 1).map((p) => (
                <th
                  key={`port-${p}`}
                  style={{
                    padding: "4px 2px",
                    border: "1px solid #e0e0e0",
                    background: "#f0f0f0",
                    fontWeight: 600,
                    fontSize: "8px",
                    minWidth: "26px",
                    position: "sticky",
                    top: 0,
                  }}
                >
                  {p}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: maxSlots }, (_, i) => i + 1).map((slot) => (
              <tr key={`slot-${slot}`}>
                <td
                  style={{
                    padding: "4px 2px",
                    border: "1px solid #e0e0e0",
                    background: "#f5f5f5",
                    fontWeight: 600,
                    color: "#667eea",
                    fontSize: "9px",
                    minWidth: "45px",
                    position: "sticky",
                    left: 0,
                    zIndex: 5,
                  }}
                >
                  .{String(slot).padStart(2, "0")}
                </td>
                {Array.from({ length: maxPorts }, (_, i) => i + 1).map(
                  (port) => {
                    const slotData = grid[port]?.[slot];
                    const isActive = slotData?.active === 1;
                    const operator = slotData?.data?.opr || "";
                    const signal = slotData?.data?.sig || 0;
                    const portStr = `${port}.${String(slot).padStart(2, "0")}`;
                    const isActivating = activatingPort === portStr;

                    return (
                      <td
                        key={`${port}-${slot}`}
                        style={{
                          padding: "4px 2px",
                          border: "1px solid #e0e0e0",
                          textAlign: "center",
                          height: "32px",
                        }}
                      >
                        <div style={{ position: "relative", display: "inline-block" }}>
                          <div
                            role={isStaff ? "button" : undefined}
                            style={{
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              margin: "0 auto",
                              background: isActive ? "#51cf66" : "#d0d0d0",
                              color: isActive ? "white" : "#666",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "7px",
                              fontWeight: 600,
                              cursor: isStaff ? "pointer" : "default",
                              boxShadow: isActive
                                ? "0 0 8px rgba(81, 207, 102, 0.6)"
                                : "none",
                              transition: "all 0.2s",
                            }}
                            title={
                              isStaff
                                ? isActive
                                  ? `${operator}\nSignal: ${signal}/31\nClick to keep active or switch another slot`
                                  : `Activate ${portStr}`
                                : isActive
                                  ? `${operator}\nSignal: ${signal}/31`
                                  : ""
                            }
                            onClick={
                              isStaff && !isActivating
                                ? () => handleActivatePort(portStr)
                                : undefined
                            }
                            onMouseEnter={(e) => {
                              if (!isStaff) return;
                              e.currentTarget.style.transform = "scale(1.2)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          >
                            {isActivating ? (
                              <span className="spinner-border spinner-border-sm" style={{ width: "10px", height: "10px", borderWidth: "1px" }} role="status" aria-hidden="true" />
                            ) : null}
                          </div>
                          {isActive && (
                            <div
                              style={{
                                position: "absolute",
                                bottom: "125%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                background: "#333",
                                color: "#fff",
                                padding: "8px",
                                borderRadius: "6px",
                                fontSize: "10px",
                                whiteSpace: "nowrap",
                                zIndex: 100,
                                pointerEvents: "none",
                                opacity: 0,
                                transition: "opacity 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.opacity = "1")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.opacity = "0")
                              }
                            >
                              {operator}
                              <br />
                              Port {port}.{String(slot).padStart(2, "0")}
                              <br />
                              Signal: {signal}/31
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  }
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="container-fluid mt-4">
      {/* Header Controls */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <h2>Port Status Monitor</h2>

        <div className="d-flex align-items-center gap-2">
          <label className="form-label mb-0 fw-bold">Gateway:</label>
          <select
            className="form-select form-select-sm"
            style={{ width: "180px" }}
            value={selectedGateway || ""}
            onChange={(e) => setSelectedGateway(e.target.value || null)}
          >
            {gateways.length === 0 && <option value="">(Loading...)</option>}
            {gateways.map((gw) => (
              <option key={gw.id} value={gw.id}>
                {gw.name}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex gap-2">
          {/* View Toggle */}
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${viewMode === "grid" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setViewMode("grid")}
            >
              🔲 Grid View
            </button>
            <button
              type="button"
              className={`btn ${viewMode === "card" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setViewMode("card")}
            >
              📋 Card View
            </button>
          </div>

          <button
            className={`btn btn-outline-secondary btn-sm ${queueOpen ? "active" : ""}`}
            onClick={() => setQueueOpen(!queueOpen)}
          >
            {queueOpen ? "Hide Queue" : "Show Queue"}
          </button>

          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setActivationQueuePanelOpen((v) => !v)}
            style={{
              fontWeight: "bold",
              backgroundColor: activationQueuePanelOpen ? "#0f3460" : "#ffffff",
              color: activationQueuePanelOpen ? "#fff" : "#0f3460",
              border: "2px solid #0f3460",
              padding: "0.4rem 0.9rem",
              borderRadius: "0.375rem",
            }}
          >
            {activationQueuePanelOpen ? "▼ Hide Queue Inspector" : "▶ Queue Inspector"}
          </button>

          <button
            className="btn btn-primary btn-sm"
            onClick={refreshStatus}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Refreshing...
              </>
            ) : (
              "🔄 Refresh"
            )}
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger mb-4">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Gateway Status Panel */}
      {gatewayStatusOpen && (
        <div className="card mb-4">
          <div
            className="card-header d-flex justify-content-between align-items-center py-2"
            style={{ backgroundColor: "#1a1a2e", color: "white", cursor: "pointer" }}
            onClick={() => setGatewayStatusOpen(false)}
          >
            <strong>🌐 Gateway Status</strong>
            <span className="text-muted small">Click to collapse</span>
          </div>
          <div className="card-body p-2">
            <div className="d-flex flex-wrap gap-2 mb-3">
              {gateways.map((gw) => {
                const status = gatewayStatuses[gw.id];
                const online = status?.online;
                const lastSeen = status?.lastSeen;
                const lastChanged = status?.lastChanged;
                const unknown = !status || status.online === null;

                return (
                  <div
                    key={gw.id}
                    className="d-flex align-items-center gap-1 px-2 py-1 rounded"
                    style={{
                      backgroundColor: unknown ? "#f5f5f5" : online ? "#d4edda" : "#f8d7da",
                      border: `1px solid ${unknown ? "#ccc" : online ? "#c3e6cb" : "#f5c6cb"}`,
                      fontSize: "12px",
                    }}
                  >
                    <span style={{ fontSize: "10px" }}>
                      {unknown ? "⚪" : online ? "🟢" : "🔴"}
                    </span>
                    <strong>{gw.name}</strong>
                    {status && !unknown && (
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        {online
                          ? `online · last seen ${lastSeen?.toLocaleTimeString() || "—"}`
                          : `offline since ${lastChanged?.toLocaleTimeString() || "—"}`}
                      </span>
                    )}
                    {status && !online && status.failure_streak > 0 && (
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        · {status.failure_streak} failures
                        {status.paused ? " · paused" : ""}
                      </span>
                    )}
                    {unknown && (
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        checking...
                      </span>
                    )}
                  </div>
                );
              })}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: "11px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  checkAllGatewayStatuses();
                }}
              >
                🔄 Check Now
              </button>
            </div>

            {gatewayEvents.length > 0 && (
              <div
                style={{
                  maxHeight: "150px",
                  overflowY: "auto",
                  backgroundColor: "#1a1a2e",
                  borderRadius: "4px",
                  padding: "8px",
                  fontFamily: "monospace",
                  fontSize: "11px",
                }}
              >
                {gatewayEvents.map((ev, idx) => (
                  <div
                    key={idx}
                    style={{
                      color: ev.type === "connected" ? "#51cf66" : "#ff6b6b",
                      marginBottom: "2px",
                    }}
                  >
                    <span style={{ color: "#888" }}>[{ev.time}]</span>{" "}
                    {ev.event} — <strong>{ev.gateway}</strong>
                    {ev.error && (
                      <span style={{ color: "#ffa94d" }}> ({ev.error})</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {gatewayEvents.length === 0 && (
              <div className="text-muted" style={{ fontSize: "12px" }}>
                No connection events yet. Events will appear here when gateways connect or
                disconnect.
              </div>
            )}
          </div>
        </div>
      )}

      {!gatewayStatusOpen && (
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary mb-3"
          onClick={() => setGatewayStatusOpen(true)}
        >
          🌐 Show Gateway Status
        </button>
      )}

      {/* Activation Queue Inspector (live timers, stuck detection, event log) */}
      <ActivationQueuePanel
        gatewayId={
          selectedGateway ? parseInt(String(selectedGateway).replace(/^gw-/, ""), 10) : undefined
        }
        isOpen={activationQueuePanelOpen}
        onToggle={() => setActivationQueuePanelOpen((v) => !v)}
      />

      {/* Status Summary */}
      {!error && (
        <div className="alert alert-info mb-4">
          <div className="d-flex justify-content-between">
            <span>
              <strong>Total Ports:</strong> {portStatus.length}
              {" | "}
              <strong>Active (hardware):</strong>{" "}
              <span className="badge bg-success">
                {Object.values(activeSlots).filter((v) => v !== null).length}
              </span>
              {" | "}
              <strong>Activation:</strong>{" "}
              <span className="badge bg-success me-1">{activationSummary.active} active</span>
              <span className="badge bg-warning text-dark me-1">{activationSummary.queued} queued</span>
              <span className="badge bg-info text-dark me-1">{activationSummary.activating} activating</span>
              {activationSummary.handing_over > 0 && (
                <span className="badge bg-warning text-dark me-1">
                  {activationSummary.handing_over} handover
                </span>
              )}
              {isStaff && (
                <span className="ms-2 text-muted" style={{ fontSize: "0.9rem" }}>
                  (Staff: click a slot to activate it)
                </span>
              )}
            </span>
            <span className="text-muted">Last updated: {lastUpdate}</span>
          </div>
        </div>
      )}

      {/* Staff switch feedback */}
      {isStaff && (switchError || switchSuccess) && (
        <div
          className={`alert mb-4 ${switchError ? "alert-danger" : "alert-success"}`}
          role="alert"
        >
          {switchError ? switchError : switchSuccess}
        </div>
      )}

      {/* Queue Window */}
      {queueOpen && (
        <div className="card mb-4 border-warning">
          <div className="card-header bg-warning d-flex justify-content-between align-items-center">
            <strong>Activation Queue</strong>
            {queuesLoading && (
              <span className="spinner-border spinner-border-sm" role="status" />
            )}
          </div>
          <div className="card-body p-2">
            {Object.keys(queues || {}).length === 0 ? (
              <div className="text-muted text-center py-3">
                No queued activations
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-striped mb-0">
                  <thead>
                    <tr>
                      <th style={{ fontSize: "0.75rem" }}>Group</th>
                      <th style={{ fontSize: "0.75rem" }}>Position</th>
                      <th style={{ fontSize: "0.75rem" }}>Number</th>
                      <th style={{ fontSize: "0.75rem" }}>Status</th>
                      <th style={{ fontSize: "0.75rem" }}>ETA (s)</th>
                      <th style={{ fontSize: "0.75rem" }}>Requested</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(queues).map(([group, items]) =>
                      (items || []).map((q, idx) => (
                        <tr key={`${group}-${q.id || idx}`}>
                          <td style={{ fontSize: "0.75rem" }}>{group}</td>
                          <td style={{ fontSize: "0.75rem" }}>
                            {q.position ?? "-"}
                          </td>
                          <td style={{ fontSize: "0.75rem" }}>
                            {q.number || "-"}
                          </td>
                          <td style={{ fontSize: "0.75rem" }}>
                            <span
                              className={`badge ${
                                q.status === "active"
                                  ? "bg-success"
                                  : q.status === "activating"
                                  ? "bg-warning text-dark"
                                  : "bg-secondary"
                              }`}
                            >
                              {q.status}
                            </span>
                          </td>
                          <td style={{ fontSize: "0.75rem" }}>
                            {q.eta_seconds ?? "-"}
                          </td>
                          <td style={{ fontSize: "0.75rem" }}>
                            {q.requested_at
                              ? new Date(q.requested_at).toLocaleTimeString()
                              : "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* GRID VIEW */}
      {viewMode === "grid" && (
        <div
          style={{
            background: "#f9f9f9",
            padding: "15px",
            borderRadius: "8px",
            border: "1px solid #ddd",
          }}
        >
          {portStatus.length > 0 ? (
            renderGridTable(
              createGrid(portStatus, gatewayMaxPorts, gatewayMaxSlots),
              gatewayMaxPorts,
              gatewayMaxSlots
            )
          ) : (
            <div className="alert alert-info">No SIM data available</div>
          )}
        </div>
      )}

      {/* CARD VIEW */}
      {viewMode === "card" && (
        <div className="row g-2">
          {Array.from({ length: gatewayMaxPorts }, (_, i) => i + 1).map((portNumber) => {
            const portData = getPortData(portNumber);

            if (!portData) {
              return (
                <div key={portNumber} className="col-lg-2 col-md-3 col-sm-4 col-6">
                  <div className="card h-100 border-light">
                    <div className="card-header bg-light text-muted py-2 px-2">
                      <strong>Port {portNumber}</strong>
                    </div>
                    <div className="card-body py-2 px-2">
                      <small className="text-muted">No data</small>
                    </div>
                  </div>
                </div>
              );
            }

            const { status, signal, operator, isActive } = portData;
            const statusLabel = getStatusLabel(status);
            const colorClass = getStatusColor(status, isActive);

            return (
              <div key={portNumber} className="col-lg-2 col-md-3 col-sm-4 col-6">
                <div
                  className={`card h-100 ${
                    isActive ? "border-success border-3" : ""
                  }`}
                  style={{
                    backgroundColor: isActive
                      ? "rgba(81, 207, 102, 0.05)"
                      : "",
                  }}
                >
                  <div className={`card-header ${colorClass} text-white py-2 px-2`}>
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>Port {portNumber}</strong>
                      {isActive && (
                        <span
                          className="badge bg-white text-success"
                          style={{ fontSize: "0.65rem" }}
                        >
                          ACTIVE
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    className="card-body py-2 px-2"
                    style={{ fontSize: "0.85rem" }}
                  >
                    <div className="mb-2">
                      <small className="text-muted d-block">Status</small>
                      <div
                        className={`badge ${colorClass}`}
                        style={{ fontSize: "0.7rem" }}
                      >
                        {statusLabel}
                      </div>
                    </div>

                    <div className="mb-2">
                      <small className="text-muted d-block">Signal</small>
                      <strong>{signal}/31</strong>
                      <div
                        className="progress"
                        style={{ height: "4px", marginTop: "2px" }}
                      >
                        <div
                          className="progress-bar"
                          style={{
                            width: `${(signal / 31) * 100}%`,
                            backgroundColor:
                              signal > 20
                                ? "#51cf66"
                                : signal > 10
                                ? "#ffd43b"
                                : "#ff6b6b",
                          }}
                        />
                      </div>
                    </div>

                    <div className="mb-2">
                      <small className="text-muted d-block">Operator</small>
                      <small
                        style={{
                          wordBreak: "break-word",
                          fontSize: "0.75rem",
                          maxHeight: "40px",
                          overflow: "hidden",
                        }}
                      >
                        {operator}
                      </small>
                    </div>

                    {isActive && (
                      <div
                        className="mt-2 pt-2 border-top"
                        style={{
                          fontSize: "0.7rem",
                          color: "#51cf66",
                          fontWeight: 600,
                        }}
                      >
                        ✓ Currently Active
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 text-center text-muted">
        <small>
          Showing {selectedGateway || "no gateway selected"} - {viewMode === "grid" ? "Grid View" : "Card View"}
        </small>
      </div>
    </div>
  );
};

export default PortStatus;
